import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../api'

const INTENT_TONE: Record<string, string> = {
  mint: 'up', deposit: 'up', converge: 'accent', convert: 'accent',
  burn: 'down', withdraw: 'down', cancel: 'down',
}

/**
 * Recent chaincode activity as projected into Postgres, one row per
 * transaction. A phone gets a stacked list; once there is width for columns
 * to line up, it becomes a table.
 */
export default function Events() {
  const { t } = useTranslation()
  const events = useQuery({ queryKey: ['events'], queryFn: () => api.events(50), refetchInterval: 10_000 })
  const rows = events.data?.data ?? []

  const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <div className="page page-lg">
      <div className="panel">
        <div className="panel-head">
          <span className="grow">{t('events')}</span>
          {events.data && <span className="chip">{rows.length}</span>}
        </div>

        <div className="panel-body flush">
          {events.isLoading && (
            <div className="panel-body stack">
              {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ height: 38 }} />)}
            </div>
          )}
          {events.data && rows.length === 0 && <div className="empty">{t('noData')}</div>}

          {rows.length > 0 && (
            <>
              {/* wide: a real table */}
              <div className="only-wide" style={{ overflowX: 'auto' }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{t('block')}</th>
                      <th>{t('time')}</th>
                      <th></th>
                      <th>{t('account')}</th>
                      <th>{t('symbol')}</th>
                      <th className="right">{t('txid')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(e => (
                      <tr key={`${e.block_number}-${e.tx_id}`}>
                        <td className="num">#{e.block_number}</td>
                        <td className="num muted">{time(e.time)}</td>
                        <td><span className={`chip ${INTENT_TONE[e.name] ?? ''}`}>{e.name || 'event'}</span></td>
                        <td>{e.account || <span className="muted">—</span>}</td>
                        <td className="muted">{e.symbol || '—'}</td>
                        <td className="right mono" title={e.tx_id}>{e.tx_id.slice(0, 16)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* narrow: stacked rows */}
              <div className="only-narrow">
                {rows.map(e => (
                  <div className="list-row" key={`${e.block_number}-${e.tx_id}`}>
                    <span className={`chip ${INTENT_TONE[e.name] ?? ''}`}>{e.name || 'event'}</span>
                    <div className="grow">
                      <div className="title">
                        {e.account
                          ? <>{e.account} <span className="muted" style={{ fontWeight: 400 }}>{e.symbol ?? ''}</span></>
                          : <span className="muted">—</span>}
                      </div>
                      <div className="sub mono" title={e.tx_id}>{e.tx_id.slice(0, 18)}…</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="value">#{e.block_number}</div>
                      <div className="sub num">{time(e.time)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
